import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Search, X, ChevronDown, Users } from "lucide-react";
import { Button } from "@/shared/components/ui/Button";
import { ProfileAvatar } from "@/shared/components/ui/Avatar";
import { Spinner } from "@/shared/components/ui/Spinner";
import { formatDisplayName } from "@/shared/utils/formatters";
import { searchTaskUsers } from "@/features/tasks/services/taskService";
import { announcementService } from "@/features/announcements/services/feedService";
import { sendingOrgService } from "@/features/return-home/services/sendingOrgService";
import type { TaskUserSearchResult, TaskUserSearchFilters } from "@/features/tasks/types/task";

const COUNTRY_OPTIONS = [
  { code: "JP", key: "profile.country_jp" },
  { code: "PH", key: "profile.country_ph" },
  { code: "VN", key: "profile.country_vn" },
  { code: "ID", key: "profile.country_id" },
  { code: "MM", key: "profile.country_mm" },
  { code: "KH", key: "profile.country_kh" },
  { code: "TH", key: "profile.country_th" },
  { code: "CN", key: "profile.country_cn" },
  { code: "NP", key: "profile.country_np" },
  { code: "IN", key: "profile.country_in" },
  { code: "BD", key: "profile.country_bd" },
  { code: "LK", key: "profile.country_lk" },
];

interface SubtaskMemberPickerProps {
  value: TaskUserSearchResult[];
  onChange: (members: TaskUserSearchResult[]) => void;
}

export const SubtaskMemberPicker: React.FC<SubtaskMemberPickerProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  // ── Filter state ──────────────────────────────────────────────────────────────
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [countrySearch, setCountrySearch]         = useState("");
  const [countryOpen, setCountryOpen]             = useState(false);
  const countryRef = useRef<HTMLDivElement>(null);

  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [companySearch, setCompanySearch]         = useState("");
  const [companyOpen, setCompanyOpen]             = useState(false);
  const companyRef = useRef<HTMLDivElement>(null);
  const [companies, setCompanies]                 = useState<{ value: string; label: string }[]>([]);

  const [sendingOrg, setSendingOrg]   = useState("");
  const [sendingOrgs, setSendingOrgs] = useState<{ value: string; label: string }[]>([]);

  const [filterBatch, setFilterBatch] = useState("");
  const [results, setResults]         = useState<TaskUserSearchResult[]>([]);
  const [searching, setSearching]     = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Outside-click handlers ────────────────────────────────────────────────────
  useEffect(() => {
    if (!countryOpen) return;
    const handler = (e: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(e.target as Node))
        setCountryOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [countryOpen]);

  useEffect(() => {
    if (!companyOpen) return;
    const handler = (e: MouseEvent) => {
      if (companyRef.current && !companyRef.current.contains(e.target as Node))
        setCompanyOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [companyOpen]);

  // ── Fetch companies when picker expands ───────────────────────────────────────
  useEffect(() => {
    if (!expanded) return;
    announcementService
      .getCompaniesWithUsers()
      .then(setCompanies)
      .catch(() => setCompanies([]));
  }, [expanded]);

  // ── Fetch sending orgs when exactly 1 country selected ────────────────────────
  useEffect(() => {
    if (selectedCountries.length === 1) {
      sendingOrgService
        .getDropdown(selectedCountries[0])
        .then((data: any[]) =>
          setSendingOrgs(data.map((o: any) => ({ value: o.value ?? o.id, label: o.label ?? o.name }))),
        )
        .catch(() => setSendingOrgs([]));
    } else {
      setSendingOrgs([]);
      setSendingOrg("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedCountries)]);

  // ── Search ────────────────────────────────────────────────────────────────────
  const doSearch = useCallback(async (filters: TaskUserSearchFilters) => {
    setSearching(true);
    try {
      const data = await searchTaskUsers(filters);
      setResults(data);
      setHasSearched(true);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const triggerSearch = useCallback(
    (extraFilters?: Partial<TaskUserSearchFilters>) => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
      searchTimeout.current = setTimeout(() => {
        doSearch({
          country:     selectedCountries.length === 1 ? selectedCountries[0] : undefined,
          sending_org: sendingOrg || undefined,
          company:     selectedCompanies.length === 1 ? selectedCompanies[0] : undefined,
          batch_no:    filterBatch || undefined,
          ...extraFilters,
        });
      }, 300);
    },
    [doSearch, selectedCountries, sendingOrg, selectedCompanies, filterBatch],
  );

  useEffect(() => {
    if (!hasSearched) return;
    triggerSearch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedCountries), sendingOrg, JSON.stringify(selectedCompanies), filterBatch]);

  const toggleUser = (user: TaskUserSearchResult) => {
    const isSel = value.some((u) => u.user_id === user.user_id);
    onChange(isSel ? value.filter((u) => u.user_id !== user.user_id) : [...value, user]);
  };

  const removeSelected = (userId: string) => {
    onChange(value.filter((u) => u.user_id !== userId));
  };

  const toggleCountry = (code: string) => {
    setSelectedCountries((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code],
    );
  };

  const toggleCompany = (val: string) => {
    setSelectedCompanies((prev) =>
      prev.includes(val) ? prev.filter((c) => c !== val) : [...prev, val],
    );
  };

  const filteredCountries = COUNTRY_OPTIONS.filter((c) =>
    c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
    t(c.key).toLowerCase().includes(countrySearch.toLowerCase()),
  );

  const filteredCompanies = companies.filter((c) =>
    c.label?.toLowerCase().includes(companySearch.toLowerCase()),
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-visible">

      {/* ── Compact header: chips + toggle ───────────────────────────────────── */}
      <div className="flex items-start gap-2 px-3 py-2 bg-gray-50 rounded-lg">
        <Users className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {value.length > 0 ? (
            value.map((u) => (
              <span
                key={u.user_id}
                className="inline-flex items-center gap-1 pl-1 pr-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium"
              >
                <ProfileAvatar
                  picId={u.profile_pic_id}
                  firstName={u.first_name}
                  lastName={u.last_name}
                  size="sm"
                />
                {formatDisplayName(u.last_name, u.first_name, u.middle_name)}
                <button
                  type="button"
                  onClick={() => removeSelected(u.user_id)}
                  className="ml-0.5 text-indigo-400 hover:text-indigo-700"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))
          ) : (
            <span className="text-xs text-gray-400 italic">
              {t("tasks.subtasks.no_members_selected")}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setExpanded((p) => !p)}
          className="shrink-0 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1 whitespace-nowrap mt-0.5"
        >
          {expanded ? t("tasks.subtasks.hide_picker") : t("tasks.subtasks.select_members")}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* ── Expanded picker ───────────────────────────────────────────────────── */}
      {expanded && (
        <div className="p-3 border-t border-gray-200 grid grid-cols-2 gap-3" style={{ minHeight: 280 }}>

          {/* Left: filters */}
          <div className="flex flex-col gap-2 overflow-y-auto pr-1" style={{ maxHeight: 320 }}>

            {/* Country multi-select */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("tasks.subtasks.filter_country")}
              </label>
              {selectedCountries.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {selectedCountries.map((code) => {
                    const opt = COUNTRY_OPTIONS.find((c) => c.code === code);
                    return (
                      <span key={code} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                        {code} — {opt ? t(opt.key) : code}
                        <button type="button" onClick={() => toggleCountry(code)} className="hover:text-indigo-900">×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative" ref={countryRef}>
                <input
                  type="text"
                  value={countrySearch}
                  onChange={(e) => { setCountrySearch(e.target.value); setCountryOpen(true); }}
                  onFocus={() => setCountryOpen(true)}
                  placeholder={t("tasks.subtasks.filter_country")}
                  className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                {countryOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-36 overflow-y-auto">
                    {filteredCountries.map((c) => {
                      const isSel = selectedCountries.includes(c.code);
                      return (
                        <button
                          key={c.code}
                          type="button"
                          onClick={() => { toggleCountry(c.code); setCountrySearch(""); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${isSel ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${isSel ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                            {isSel && "✓"}
                          </span>
                          {c.code} — {t(c.key)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Sending Org (when 1 country selected) */}
            {sendingOrgs.length > 0 && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t("tasks.subtasks.filter_sending_org")}
                </label>
                <div className="relative">
                  <select
                    value={sendingOrg}
                    onChange={(e) => setSendingOrg(e.target.value)}
                    className="w-full pl-3 pr-8 py-1.5 text-xs border border-gray-300 rounded-lg appearance-none focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                  >
                    <option value="">—</option>
                    {sendingOrgs.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Company multi-select */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("tasks.subtasks.filter_company")}
              </label>
              {selectedCompanies.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {selectedCompanies.map((val) => {
                    const label = companies.find((c) => c.value === val)?.label ?? val;
                    return (
                      <span key={val} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                        {label}
                        <button type="button" onClick={() => toggleCompany(val)} className="hover:text-indigo-900">×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative" ref={companyRef}>
                <input
                  type="text"
                  value={companySearch}
                  onChange={(e) => { setCompanySearch(e.target.value); setCompanyOpen(true); }}
                  onFocus={() => setCompanyOpen(true)}
                  placeholder={t("tasks.subtasks.filter_company")}
                  className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                {companyOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-36 overflow-y-auto">
                    {filteredCompanies.map((c) => {
                      const isSel = selectedCompanies.includes(c.value);
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => { toggleCompany(c.value); setCompanySearch(""); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${isSel ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}
                        >
                          <span className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${isSel ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                            {isSel && "✓"}
                          </span>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Batch */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t("tasks.subtasks.filter_batch")}
              </label>
              <input
                type="text"
                value={filterBatch}
                onChange={(e) => setFilterBatch(e.target.value)}
                placeholder={t("tasks.subtasks.filter_batch")}
                className="w-full px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>

            {/* Search button */}
            <Button
              type="button"
              variant="secondary"
              onClick={() => triggerSearch()}
              disabled={searching}
              className="w-full flex items-center justify-center gap-2"
            >
              {searching ? <Spinner size="sm" /> : <Search className="w-4 h-4" />}
              {t("tasks.subtasks.search_btn")}
            </Button>
          </div>

          {/* Right: search results */}
          <div className="border border-gray-200 rounded-lg overflow-y-auto" style={{ maxHeight: 320 }}>
            {!hasSearched ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 p-4">
                <Search className="w-8 h-8 opacity-40" />
                <p className="text-xs text-center">{t("tasks.subtasks.search_placeholder")}</p>
              </div>
            ) : searching ? (
              <div className="flex items-center justify-center h-full">
                <Spinner size="sm" />
              </div>
            ) : results.length === 0 ? (
              <div className="flex items-center justify-center h-full text-xs text-gray-400 p-4">
                {t("common.search_e")}
              </div>
            ) : (
              results.map((user) => {
                const isSel = value.some((u) => u.user_id === user.user_id);
                return (
                  <button
                    key={user.user_id}
                    type="button"
                    onClick={() => toggleUser(user)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors text-left ${isSel ? "bg-indigo-50" : ""}`}
                  >
                    <ProfileAvatar
                      picId={user.profile_pic_id}
                      firstName={user.first_name}
                      lastName={user.last_name}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {formatDisplayName(user.last_name, user.first_name, user.middle_name)}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {[user.user_type, user.company, user.batch_no].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    {isSel && <span className="text-indigo-600 text-xs font-medium shrink-0">✓</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};
